import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { 
  Settings, ShieldAlert, CheckCircle, Clock, ExternalLink, 
  Key, RefreshCw, Play, AlertCircle, Check, Info, FileText,
  User, ShieldCheck, ChevronDown, ChevronUp, HelpCircle,
  Briefcase, TrendingUp, Cpu, Eye, BookOpen, Layers
} from 'lucide-react';

interface ProviderStatus {
  provider: string;
  status: string;
  requiredCredentials: string[];
  missingCredentials: string[];
  isConfigured: boolean;
  last_test_at: string | null;
  last_test_result: string | null;
  last_error: string | null;
}

export function SocialConnectorsConsole() {
  const { user } = useAuth();
  const [providers, setProviders] = useState<Record<string, ProviderStatus>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [dryRunningProvider, setDryRunningProvider] = useState<string | null>(null);
  const [dryRunPayload, setDryRunPayload] = useState<any | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Interface Mode toggles
  const [isAdvancedMode, setIsAdvancedMode] = useState<boolean>(false);
  const [openAccordion, setOpenAccordion] = useState<Record<string, boolean>>({});
  const [showClientPreview, setShowClientPreview] = useState<boolean>(false);

  // Client Preview Mock States
  const [clientConnected, setClientConnected] = useState<Record<string, boolean>>({
    facebook: false,
    instagram: false,
    linkedin: false,
    tiktok: false
  });
  const [campaignApproved, setCampaignApproved] = useState<boolean>(false);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const getHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    const stagingEmail = localStorage.getItem("nexorax_bypassed_user_email") || 
                         localStorage.getItem("x-operator-staging-email");
    if (stagingEmail) {
      headers['x-operator-staging-email'] = stagingEmail.toLowerCase().trim();
    }
    if (user) {
      try {
        const token = await user.getIdToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (e) {
        console.warn("Failed to retrieve ID token:", e);
      }
    }
    return headers;
  };

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const res = await fetch('/api/admin/social-connectors/status', { headers });
      if (!res.ok) {
        const errText = await res.text();
        let errMsg = 'Failed to fetch social connectors status';
        try {
          const errJSON = JSON.parse(errText);
          errMsg = errJSON.error || errMsg;
        } catch (e) {}
        throw new Error(errMsg);
      }
      const data = await res.json();
      setProviders(data.providers || {});
    } catch (err: any) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [user]);

  const handleTestConnection = async (provider: string) => {
    setTestingProvider(provider);
    try {
      const headers = await getHeaders();
      const res = await fetch(`/api/admin/social-connectors/test/${provider}`, {
        method: 'POST',
        headers
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to complete connection test');
      }

      showToast(`Connection test for ${providerNamesMap[provider]} passed successfully!`);
      fetchStatus();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setTestingProvider(null);
    }
  };

  const handleDryRun = async (provider: string) => {
    setTestingProvider(null);
    setDryRunningProvider(provider);
    setDryRunPayload(null);
    try {
      const headers = await getHeaders();
      
      const mockPayload = {
        campaignItemId: `item-mock-${provider}-${Date.now()}`,
        title: `NexoraX Launch Campaign - ${providerNamesMap[provider]}`,
        body: `Unlock the future of managed digital services and B2B automation. Dynamic system designs tailored for scaling. High-fidelity portals from £25/month. Contact info@nexoraxdigital.co.uk.`,
        assetName: 'nexorax-square-campaign-contact-sheet.png'
      };

      const res = await fetch(`/api/admin/social-connectors/dry-run/${provider}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(mockPayload)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Dry run post failed');
      }

      setDryRunPayload(data.payloadPreview);
      showToast(`Dry run payload successfully prepared for ${providerNamesMap[provider]}!`);
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setDryRunningProvider(null);
    }
  };

  const providerNamesMap: Record<string, string> = {
    meta: 'Facebook Page',
    instagram: 'Instagram Business',
    whatsapp: 'WhatsApp Business Cloud API',
    linkedin: 'LinkedIn',
    x: 'X / Twitter',
    youtube: 'YouTube',
    tiktok: 'TikTok Business',
    google_business: 'Google Business Profile',
    pinterest: 'Pinterest',
    threads: 'Threads Meta Connector',
    email_sender: 'Email Campaign Sender',
    sms_provider: 'SMS Provider API'
  };

  // Beginner friendly credential error formatter
  const getFriendlyEnvMessage = (keyName: string) => {
    const mappers: Record<string, string> = {
      META_APP_ID: "Meta Application ID is missing. Register inside Meta Developer Portal and add it server-side.",
      META_APP_SECRET: "Meta Application Secret is missing. Generate it inside Meta Developer Portal and add it server-side.",
      META_PAGE_ID: "Facebook Page ID is missing. Look it up in Page settings and add it on the server.",
      META_PAGE_ACCESS_TOKEN: "Facebook Page access token is missing. Create a permanent system token and add it on the server.",
      META_VERIFY_TOKEN: "Webhook Verify Token is missing. Add it on the server.",
      META_INSTAGRAM_BUSINESS_ACCOUNT_ID: "Instagram Business Account ID is missing. Link your Instagram account to Facebook Page first.",
      WHATSAPP_BUSINESS_ACCOUNT_ID: "WhatsApp Business Account ID is missing. Locate it in Meta Business Manager and add it server-side.",
      WHATSAPP_PHONE_NUMBER_ID: "WhatsApp Phone ID is missing. Locate it in Developer Portal and add it server-side.",
      WHATSAPP_ACCESS_TOKEN: "WhatsApp Access Token is missing. Generate it in Meta Developer Portal and add it server-side.",
      LINKEDIN_CLIENT_ID: "LinkedIn Client ID is missing. Create an App in Developer Portal and add it on the server.",
      LINKEDIN_CLIENT_SECRET: "LinkedIn Client Secret is missing. Create an App in Developer Portal and add it on the server.",
      LINKEDIN_ORGANIZATION_ID: "LinkedIn Organisation ID is missing. Lookup organization ID in page URL and add it on the server.",
      LINKEDIN_ACCESS_TOKEN: "LinkedIn Access Token is missing. Perform OAuth login to retrieve and add it on the server.",
      X_API_KEY: "X API Key is missing. Create a Developer Account and add it on the server.",
      X_API_SECRET: "X API Secret is missing. Create a Developer Account and add it on the server.",
      X_ACCESS_TOKEN: "X Access Token is missing. Add it on the server first.",
      X_ACCESS_TOKEN_SECRET: "X Access Token Secret is missing. Add it on the server first.",
      X_BEARER_TOKEN: "X Bearer Token is missing. Add it on the server first.",
      GOOGLE_CLIENT_ID: "Google Client ID is missing. Set up OAuth in Google Cloud Console and add it on the server.",
      GOOGLE_CLIENT_SECRET: "Google Client Secret is missing. Set up OAuth in Google Cloud Console and add it on the server.",
      YOUTUBE_CHANNEL_ID: "YouTube Channel ID is missing. Lookup channel ID in account settings and add it on the server.",
      YOUTUBE_REFRESH_TOKEN: "YouTube Refresh Token is missing. Perform OAuth login to obtain and add it on the server."
    };
    return mappers[keyName] || `${keyName} credential is missing. Check server environment.`;
  };

  // Plain English checklists
  const checklistsMap: Record<string, string[]> = {
    meta: [
      "Create or choose NexoraX Facebook Page",
      "Create Meta Developer App",
      "Add Page posting permission",
      "Add Page ID and access token server-side",
      "Test connection",
      "Run dry post test",
      "Enable posting only after approval"
    ],
    instagram: [
      "Connect Instagram Business account to Facebook Page",
      "Add Instagram Business Account ID server-side",
      "Test connection",
      "Run dry post test"
    ],
    whatsapp: [
      "Use WhatsApp Business Cloud API",
      "Add Business Account ID",
      "Add Phone Number ID",
      "Add Access Token server-side",
      "Templates may be required for outgoing messages",
      "Test connection"
    ],
    linkedin: [
      "Create LinkedIn Developer App",
      "Connect personal/company account",
      "Add organisation ID/token server-side",
      "Test connection"
    ],
    x: [
      "Create X Developer App",
      "Add API keys/tokens server-side",
      "Test connection"
    ],
    youtube: [
      "Create Google Cloud project",
      "Enable YouTube Data API",
      "Add OAuth credentials server-side",
      "Test connection"
    ],
    tiktok: [
      "Create TikTok Developer Account",
      "Register client key/secret",
      "Add Direct Posting API scopes",
      "Test connection dry-runs"
    ],
    google_business: [
      "Verify location in Google Business Profile",
      "Enable Google My Business API Scopes",
      "Add partner authorization key",
      "Publish business updates"
    ],
    pinterest: [
      "Register Pinterest Developer App",
      "Obtain Board ID and Board tokens",
      "Add assets for Pin posting",
      "Test dry-run Pin generation"
    ],
    threads: [
      "Enable Meta Threads API Developer scopes",
      "Add Threads Token server-side",
      "Format posts for Threads text formatting",
      "Dry test post"
    ],
    email_sender: [
      "Configure SendGrid API or SMTP server connections",
      "Add verified Sender domain signatures",
      "Test transactional mail pathways",
      "Queue automated outreach"
    ],
    sms_provider: [
      "Create Twilio Account or SMS Gateway credentials",
      "Configure Phone Numbers and Webhooks",
      "Validate SMS text templates",
      "Track message delivery rates"
    ]
  };

  // Simple Mode Status Classifier
  const getSimpleStatus = (prov: ProviderStatus) => {
    if (prov.status === 'Test Passed') {
      return { label: 'Dry Run Available', color: 'text-emerald-400 border-emerald-500/20 bg-emerald-950/10' };
    }
    if (prov.status === 'Coming Soon') {
      return { label: 'Coming Soon', color: 'text-purple-400 border-purple-500/20 bg-purple-950/10' };
    }
    if (prov.status === 'Future Connector') {
      return { label: 'Future API Connector', color: 'text-cyan-400 border-cyan-500/20 bg-cyan-950/10' };
    }
    if (prov.status === 'Manual Mode Available') {
      return { label: 'Manual Mode Available', color: 'text-slate-300 border-slate-700 bg-slate-800/20' };
    }
    
    if (!prov.isConfigured) {
      if (prov.missingCredentials && prov.missingCredentials.length < prov.requiredCredentials.length) {
        return { label: 'Missing Details', color: 'text-yellow-400 border-yellow-500/20 bg-yellow-950/10' };
      }
      return { label: 'Needs Account Connection', color: 'text-slate-400 border-slate-700/30 bg-slate-900/25' };
    }
    return { label: 'Ready for Setup', color: 'text-blue-400 border-blue-500/20 bg-blue-950/10' };
  };

  // Recommendations Engine (Next simplest step)
  const getNextSimplestStep = () => {
    const list = ['meta', 'instagram', 'whatsapp', 'linkedin', 'x', 'youtube'];
    for (const p of list) {
      const prov = providers[p];
      if (!prov || !prov.isConfigured) {
        return {
          provider: p,
          tip: p === 'meta' 
            ? "Start with Facebook Page. Create/confirm your NexoraX Facebook Page, then add Meta developer credentials server-side."
            : `Set up credentials for ${providerNamesMap[p]}. Gather the required details on the server to enable connection testing.`
        };
      }
      if (prov.status !== 'Test Passed') {
        return {
          provider: p,
          tip: `Run a connection test for ${providerNamesMap[p]}. Once the connection works, perform a dry run post verification.`
        };
      }
    }
    return {
      provider: null,
      tip: "All platforms successfully integrated! Live API posting is locked until official permissions are enabled."
    };
  };

  const nextStep = getNextSimplestStep();

  const toggleAccordion = (provider: string) => {
    setOpenAccordion(prev => ({
      ...prev,
      [provider]: !prev[provider]
    }));
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center font-mono text-purple-400">
        <Clock className="mr-2 h-5 w-5 animate-spin text-purple-400" />
        LOADING SETUP WIZARD ENVIRONMENT...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-950/20 p-6 text-red-300">
        <ShieldAlert className="mb-2 h-8 w-8 text-red-500" />
        <h3 className="font-mono text-lg font-bold">SETUP ENGINE OFFLINE</h3>
        <p className="mt-2 text-sm">{error}</p>
        <button 
          onClick={fetchStatus} 
          className="mt-4 rounded bg-red-900/40 px-4 py-2 text-xs font-bold hover:bg-red-900/60"
        >
          Retry Load
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Alert */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg px-4 py-3 shadow-lg border transition-all ${
          toast.type === 'success' 
            ? 'bg-emerald-950/90 border-emerald-500/30 text-emerald-300' 
            : 'bg-red-950/90 border-red-500/30 text-red-300'
        }`}>
          <Info className="h-4 w-4" />
          <span className="font-mono text-xs font-black">{toast.message}</span>
        </div>
      )}

      {/* Main Header / Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-800 pb-4">
        <div>
          <h1 className="font-mono text-xl font-bold text-slate-100 flex items-center gap-2">
            <Settings className="h-6 w-6 text-purple-400" />
            Social Posting Setup Wizard
          </h1>
          <p className="text-xs text-slate-400 mt-1 font-mono">
            Connect official platform accounts step by step. Nothing posts automatically. Every post requires Director approval.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex gap-2">
          <button
            onClick={() => setShowClientPreview(!showClientPreview)}
            className={`rounded-lg border px-3 py-1.5 font-mono text-xxs font-bold transition-all ${
              showClientPreview 
                ? 'bg-emerald-950/30 border-emerald-500/40 text-emerald-300 hover:bg-emerald-900/30' 
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800'
            }`}
          >
            {showClientPreview ? 'Hide Client Preview' : 'Show Client Preview'}
          </button>
          <button
            onClick={() => setIsAdvancedMode(!isAdvancedMode)}
            className={`rounded-lg border px-3 py-1.5 font-mono text-xxs font-bold transition-all ${
              isAdvancedMode 
                ? 'bg-purple-950/30 border-purple-500/40 text-purple-300 hover:bg-purple-900/30' 
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800'
            }`}
          >
            {isAdvancedMode ? 'Switch to Simple Mode' : 'Switch to Developer Mode'}
          </button>
        </div>
      </div>

      {/* Safety Policy active banner */}
      <div className="relative overflow-hidden rounded-xl border border-yellow-500/20 bg-yellow-950/10 p-5 shadow-[0_0_15px_rgba(234,179,8,0.05)]">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-yellow-500/10 p-2 text-yellow-400 shrink-0">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-yellow-400">
              SAFETY POLICY ACTIVE (STAGING ENVIRONMENT ONLY)
            </h3>
            <p className="text-xxs text-slate-400 leading-relaxed font-mono">
              Automatic posting requires official platform APIs and account permissions. NexoraX will never ask customers for platform passwords. Customers will connect accounts using official authorization flows when available.
            </p>
          </div>
        </div>
      </div>

      {/* Client Preview Mode */}
      {showClientPreview && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-950/5 p-6 space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
            <Eye className="h-5 w-5 text-emerald-400" />
            <h3 className="font-mono text-sm font-bold text-slate-200 uppercase tracking-wider">Client Preview Portal (Mockup)</h3>
          </div>
          <p className="text-xxs text-slate-400 font-mono">
            How future customers will link social accounts, preview planned campaign updates, and authorize API dispatches directly.
          </p>

          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4 font-mono text-[10px]">
            {[
              { id: 'facebook', name: 'Connect Facebook' },
              { id: 'instagram', name: 'Connect Instagram' },
              { id: 'linkedin', name: 'Connect LinkedIn' },
              { id: 'tiktok', name: 'Connect TikTok' }
            ].map(c => (
              <button
                key={c.id}
                onClick={() => {
                  setClientConnected(prev => ({ ...prev, [c.id]: !prev[c.id] }));
                  showToast(`${c.name} mock connection toggled.`);
                }}
                className={`p-3 rounded-lg border flex items-center justify-between transition-all ${
                  clientConnected[c.id]
                    ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-300'
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                <span>{c.name}</span>
                {clientConnected[c.id] ? <Check className="h-4 w-4" /> : <ExternalLink className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>

          <div className="p-4 rounded-lg bg-slate-950/50 border border-slate-850 space-y-3 font-mono text-xxs">
            <div className="flex justify-between items-center">
              <span className="text-slate-400 uppercase font-bold">Planned Campaign Update Preview</span>
              <span className="text-slate-500">[Mock Campaign ID: SME-Website-LaunchCare]</span>
            </div>
            <div className="text-slate-300 bg-slate-950 p-3 rounded border border-slate-900 leading-relaxed">
              <strong>NexoraX Launch Care:</strong> Keep your business platform fast, secure, and up-to-date starting from £25/month. Get high-fidelity B2B custom websites tailored for immediate customer acquisition.
            </div>

            <div className="flex justify-between items-center gap-3 pt-2">
              <div className="text-slate-400">
                Mock Approval Status: <strong className={campaignApproved ? 'text-emerald-400' : 'text-yellow-400'}>{campaignApproved ? 'APPROVED FOR POSTING' : 'PENDING CLIENT APPROVAL'}</strong>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setCampaignApproved(!campaignApproved);
                    showToast(campaignApproved ? 'Campaign approval withdrawn.' : 'Campaign approved! Staging dispatch ready.');
                  }}
                  className={`px-3 py-1.5 rounded font-bold ${
                    campaignApproved 
                      ? 'bg-yellow-950/40 border border-yellow-500/25 text-yellow-400' 
                      : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  }`}
                >
                  {campaignApproved ? 'Revoke Approval' : 'Approve Campaign'}
                </button>
                <button
                  onClick={() => showToast("Client reports are coming soon! Set up platform API credentials to fetch analytics.")}
                  className="px-3 py-1.5 rounded bg-slate-850 border border-slate-800 text-slate-300 hover:bg-slate-800"
                >
                  View Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mode Explanations (End Client vs. Director) */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* End Client Mode Card */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/10 p-5 space-y-3">
          <h4 className="font-mono text-xs font-black text-slate-200 uppercase tracking-wider flex items-center gap-2">
            <User className="h-4 w-4 text-purple-400" />
            End Client Mode Flow
          </h4>
          <ol className="space-y-1.5 font-mono text-[10px] text-slate-400 list-decimal pl-4">
            <li>Connect account via official secure OAuth gateway</li>
            <li>Choose from pre-compiled marketing campaigns</li>
            <li>Preview planned message copy and visuals</li>
            <li>Approve post with single-tap credentials</li>
            <li>System automatically schedules & posts through API</li>
            <li>Track live impressions, clicks, and reply leads</li>
          </ol>
        </div>

        {/* Director Mode Card */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/10 p-5 space-y-3">
          <h4 className="font-mono text-xs font-black text-slate-200 uppercase tracking-wider flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-purple-400" />
            Director Mode Flow (NexoraX Internal)
          </h4>
          <ol className="space-y-1.5 font-mono text-[10px] text-slate-400 list-decimal pl-4">
            <li>Prepare launch copy pack in marketing folder</li>
            <li>Copy text and manually open social platforms</li>
            <li>Generate & dry-run JSON payload structures</li>
            <li>Configure environment credentials server-side</li>
            <li>Trigger connection testing tool</li>
            <li>Perform API-level post only after explicit sign-off</li>
          </ol>
        </div>
      </div>

      {/* Recommendations engine tip box */}
      <div className="rounded-xl border border-purple-500/20 bg-purple-950/5 p-5 space-y-2">
        <h4 className="font-mono text-xs font-bold text-purple-300 uppercase tracking-wider flex items-center gap-1.5">
          <HelpCircle className="h-4 w-4" />
          Recommended Next Step
        </h4>
        <p className="text-xxs text-slate-300 font-mono leading-relaxed">
          {nextStep.tip}
        </p>
      </div>

      {/* Platform Cards Section */}
      {!isAdvancedMode ? (
        // SIMPLE MODE (DEFAULT)
        <div className="space-y-6">
          <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-400">Platform Accounts Wizard</h3>
          <div className="grid gap-6 md:grid-cols-2">
            {Object.keys(providerNamesMap).map(pKey => {
              const prov = providers[pKey] || {
                provider: pKey,
                status: pKey === 'tiktok' || pKey === 'threads' ? 'Coming Soon' : 
                        pKey === 'google_business' || pKey === 'pinterest' || pKey === 'sms_provider' ? 'Future Connector' :
                        pKey === 'email_sender' ? 'Manual Mode Available' : 'Not Connected',
                requiredCredentials: [],
                missingCredentials: [],
                isConfigured: false,
                last_test_at: null,
                last_test_result: null,
                last_error: null
              };

              const simpleStatus = getSimpleStatus(prov);
              const checklist = checklistsMap[pKey] || [];
              const isFuture = ['tiktok', 'google_business', 'pinterest', 'threads', 'sms_provider'].includes(pKey);

              return (
                <div key={pKey} className="rounded-xl border border-slate-800 bg-slate-900/35 p-5 space-y-4 backdrop-blur-md flex flex-col justify-between">
                  <div className="space-y-4">
                    {/* Card Header */}
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-mono text-sm font-bold text-slate-200">{providerNamesMap[pKey]}</h3>
                        <span className="font-mono text-[9px] text-slate-500 uppercase tracking-widest">Platform Account</span>
                      </div>

                      <div className="flex flex-col items-end gap-1.5">
                        <span className={`rounded px-2.5 py-0.5 font-mono text-[10px] font-bold border uppercase ${simpleStatus.color}`}>
                          {simpleStatus.label}
                        </span>
                        <div className="flex gap-1">
                          <span className="rounded bg-slate-950/40 border border-slate-800/80 px-1.5 py-0.5 font-mono text-[8px] text-slate-500 uppercase font-black">
                            Approval Required
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Checklist */}
                    <div className="rounded bg-slate-950/40 p-4 border border-slate-900/50 space-y-2">
                      <span className="block font-mono text-[9px] font-black text-slate-400 uppercase tracking-wider">
                        Setup Checklist
                      </span>
                      <ul className="space-y-1.5 font-mono text-[10px] text-slate-400">
                        {checklist.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="h-3.5 w-3.5 text-purple-400 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Missing credentials display */}
                    {!isFuture && prov.missingCredentials && prov.missingCredentials.length > 0 && (
                      <div className="rounded bg-red-950/5 p-3 border border-red-900/10 space-y-1.5">
                        <span className="block font-mono text-[9px] font-black text-red-400 uppercase tracking-wider">
                          Missing Credentials:
                        </span>
                        <div className="space-y-1 font-mono text-[10px] text-red-300">
                          {prov.missingCredentials.map(key => (
                            <div key={key} className="flex items-start gap-1.5">
                              <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />
                              <span>{getFriendlyEnvMessage(key)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Advanced Developer Details Accordion */}
                    <div className="border-t border-slate-900 pt-3">
                      <button
                        onClick={() => toggleAccordion(pKey)}
                        className="w-full flex items-center justify-between text-slate-500 hover:text-slate-400 font-mono text-[9px] uppercase font-black"
                      >
                        <span>Advanced Developer Details</span>
                        {openAccordion[pKey] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </button>

                      {openAccordion[pKey] && (
                        <div className="mt-3 rounded bg-black/35 p-3 font-mono text-[9px] text-slate-500 space-y-2 border border-slate-900/60">
                          <div>
                            <strong>Technical ID:</strong> <code className="text-purple-400">{pKey}</code>
                          </div>
                          <div>
                            <strong>Required Env keys:</strong>
                            <ul className="list-disc pl-4 mt-1 text-[8px] uppercase">
                              {prov.requiredCredentials.map(v => (
                                <li key={v} className={prov.missingCredentials && prov.missingCredentials.includes(v) ? 'text-red-400/80' : 'text-emerald-400/80'}>
                                  {v}
                                </li>
                              ))}
                            </ul>
                          </div>
                          {prov.last_test_at && (
                            <div>
                              <strong>Last Run:</strong> {new Date(prov.last_test_at).toLocaleString()}
                            </div>
                          )}
                          {prov.last_error && (
                            <div className="text-red-400">
                              <strong>Raw Log Error:</strong> {prov.last_error}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Primary Action Button */}
                  <div className="mt-4 pt-4 border-t border-slate-900">
                    {isFuture ? (
                      <button
                        disabled={true}
                        className="w-full rounded-lg bg-slate-950 border border-slate-900 py-2.5 font-mono text-xxs font-bold text-slate-600 cursor-not-allowed"
                      >
                        Coming Soon / Platform Setup Locked
                      </button>
                    ) : !prov.isConfigured ? (
                      <button
                        onClick={() => {
                          toggleAccordion(pKey);
                          showToast(`Configuration is missing. View Advanced details to check server keys.`, 'error');
                        }}
                        className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 py-2.5 font-mono text-xxs font-bold text-slate-300 transition-all"
                      >
                        <Info className="h-3.5 w-3.5 text-slate-400" />
                        View Missing Details
                      </button>
                    ) : prov.status !== 'Test Passed' ? (
                      <button
                        disabled={testingProvider === pKey}
                        onClick={() => handleTestConnection(pKey)}
                        className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-purple-600/90 hover:bg-purple-500 py-2.5 font-mono text-xxs font-bold text-white transition-all shadow-md"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${testingProvider === pKey ? 'animate-spin' : ''}`} />
                        Test Connection
                      </button>
                    ) : (
                      <button
                        disabled={dryRunningProvider === pKey}
                        onClick={() => handleDryRun(pKey)}
                        className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 py-2.5 font-mono text-xxs font-black text-white transition-all shadow-md"
                      >
                        <Play className="h-3.5 w-3.5" />
                        Run Dry Test
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // ADVANCED MODE (DEVELOPER VIEW)
        <div className="grid gap-6 md:grid-cols-2">
          {Object.keys(providerNamesMap).map(pKey => {
            const prov = providers[pKey] || {
              provider: pKey,
              status: 'Not Connected',
              requiredCredentials: [],
              missingCredentials: [],
              isConfigured: false,
              last_test_at: null,
              last_test_result: null,
              last_error: null
            };

            return (
              <div key={pKey} className="rounded-xl border border-slate-800 bg-slate-900/35 p-5 space-y-4 backdrop-blur-md">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-mono text-sm font-black text-slate-200">{providerNamesMap[pKey]}</h4>
                    <span className="font-mono text-[9px] text-purple-400 uppercase tracking-wider">DEV_PROVIDER_ID: {pKey}</span>
                  </div>

                  <span className="rounded bg-slate-950/40 border border-slate-800/80 px-2 py-0.5 font-mono text-[9px] text-slate-400 uppercase font-bold">
                    {prov.status}
                  </span>
                </div>

                <div className="rounded bg-slate-950/40 p-3 border border-slate-900/50 space-y-2">
                  <span className="block font-mono text-[9px] font-black text-slate-400 uppercase tracking-wider">
                    Technical Env Checklist
                  </span>
                  <div className="grid gap-1 font-mono text-[10px]">
                    {prov.requiredCredentials.map(key => {
                      const isMissing = prov.missingCredentials && prov.missingCredentials.includes(key);
                      return (
                        <div key={key} className="flex items-center justify-between py-0.5 border-b border-slate-900/30 last:border-b-0">
                          <span className="text-slate-400">{key}</span>
                          {isMissing ? (
                            <span className="text-red-500 font-bold flex items-center gap-1 shrink-0">
                              <AlertCircle className="h-3 w-3" /> MISSING
                            </span>
                          ) : (
                            <span className="text-emerald-400 font-bold flex items-center gap-1 shrink-0">
                              <Check className="h-3 w-3" /> CONFIGURED
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Dev Action buttons */}
                <div className="flex gap-2 pt-2 border-t border-slate-900/40">
                  <button
                    disabled={!prov.isConfigured || testingProvider === pKey}
                    onClick={() => handleTestConnection(pKey)}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30 py-2 font-mono text-xxs font-bold text-slate-300 transition-all"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Test Connection
                  </button>
                  <button
                    disabled={!prov.isConfigured || dryRunningProvider === pKey}
                    onClick={() => handleDryRun(pKey)}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded bg-purple-600/80 hover:bg-purple-500 disabled:opacity-30 py-2 font-mono text-xxs font-black text-white transition-all"
                  >
                    <Play className="h-3 w-3" />
                    Dry Run Post
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Future Automation Features Grid */}
      <div className="space-y-4 pt-6 border-t border-slate-800">
        <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-400">Future Automation Features (Roadmap)</h3>
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-5 text-xxs font-mono">
          {[
            "Auto-schedule posts", "Post approval queue", "Multi-platform calendar", 
            "Reels/Shorts video queue", "Hashtag generator", "Caption variants", 
            "QR campaign tracking", "UTM link tracking", "Lead capture from replies", 
            "Customer connection portal", "Client campaign dashboard", "Monthly report export", 
            "Failed post retry queue", "Compliance/opt-out check", "Team approval workflow", 
            "AI content suggestions", "Asset library picker", "Brand voice presets", 
            "Manual fallback mode"
          ].map(f => (
            <div key={f} className="rounded-lg border border-slate-800 bg-slate-900/25 p-3 flex flex-col justify-between border-dashed">
              <span className="text-slate-300">{f}</span>
              <span className="text-[8px] font-bold text-purple-400 uppercase mt-2">Planned Feature</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sellability Sections */}
      <div className="grid gap-6 md:grid-cols-2 pt-6 border-t border-slate-800 font-mono text-xxs">
        {/* Sell Now */}
        <div className="rounded-xl border border-emerald-500/10 bg-emerald-950/5 p-5 space-y-3">
          <h4 className="font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
            <TrendingUp className="h-4 w-4" />
            What can we sell now?
          </h4>
          <ul className="space-y-1 text-slate-300 pl-4 list-disc">
            <li>Social media campaign packs</li>
            <li>Managed posting support</li>
            <li>Content calendars</li>
            <li>Captions, hooks, and hashtags</li>
            <li>QR campaign assets</li>
            <li>Manual/semi-automated launch support</li>
          </ul>
        </div>

        {/* Sell Later */}
        <div className="rounded-xl border border-purple-500/10 bg-purple-950/5 p-5 space-y-3">
          <h4 className="font-bold text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
            <Briefcase className="h-4 w-4" />
            What becomes sellable after API setup?
          </h4>
          <ul className="space-y-1 text-slate-300 pl-4 list-disc">
            <li>Approval-based auto-posting</li>
            <li>Scheduled posting</li>
            <li>Customer connected accounts</li>
            <li>Campaign reporting</li>
            <li>Multi-platform dispatch</li>
            <li>Reply/lead tracking</li>
          </ul>
        </div>
      </div>

      {/* Dry Run Preview Section */}
      {dryRunPayload && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/20 p-5 space-y-3">
          <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
            <FileText className="h-4 w-4 text-purple-400" />
            <h4 className="font-mono text-xs font-bold text-slate-300 uppercase">Staged API Payload Preview (Dry-Run Mode)</h4>
          </div>
          <pre className="rounded bg-black/50 p-4 font-mono text-xxs text-purple-300 overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-96 custom-scrollbar border border-purple-500/10">
            {JSON.stringify(dryRunPayload, null, 2)}
          </pre>
          <div className="flex justify-between items-center text-xxs font-mono text-slate-500">
            <span>Payload validated and structured locally in staging store.</span>
            <button 
              disabled={true} 
              className="rounded bg-slate-850 border border-slate-850/80 px-3 py-1 font-bold text-slate-650 cursor-not-allowed"
            >
              Approve for API Posting
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
